import { BidPointViewModel } from "api";
import {
    bidCalculationStatusesEnum,
    bidPointTypesEnum,
    bidRefuelingServiceTypesEnum,
    bidStatusesEnum,
    bidTypesEnum,
    featureModuleTypesEnum,
    organizationFlags,
    organizationFlagsEnum,
    syncEntityStatusesEnum,
} from "helpers/enums";
import { isTechSupport } from "helpers/hiddenData";
import { isOnlyInRole } from "helpers/roles";
import { flagVal } from "helpers/system";
import pundit from "./pundit";

const headLogistCanEditCompletedBidsFlagVal = flagVal(
    organizationFlags,
    organizationFlagsEnum.HeadLogistCanEditCompletedBids,
);
const prohibitionOfRevertingBidForHeadLogistFlagVal = flagVal(
    organizationFlags,
    organizationFlagsEnum.ProhibitionOfRevertingBidForHeadLogist,
);
const prohibitionOfBidDeleteFlagVal = flagVal(organizationFlags, organizationFlagsEnum.ProhibitionOfBidDelete);

const BidPolicy = pundit({
    notOwner: ({ user: { profile }, record: { organizationId } }) => organizationId !== profile!.organizationId,
    isOwner: ({ user: { profile }, record: { organizationId }, options: { action } }) => {
        return action !== "edit" || organizationId === profile!.organizationId;
    },
    canSeeBookFields: ({ user: { roles }, context }) => {
        const { isForwarder } = roles;
        return (
            !context.inFinishedStatus() &&
            (context.isPrivateOrPublicTruckingOrgBid() || !isForwarder) &&
            context.isCargoTruck()
        );
    },
    inFinishedStatus: ({ record: { status } }) => {
        const { Done, Canceled } = bidStatusesEnum;
        return [Done, Canceled].includes(status);
    },
    isCargoOwner: ({ user: { roles } }) => {
        const { isCargoOwnerAdmin, isCargoOwnerManager } = roles;
        return isCargoOwnerAdmin || isCargoOwnerManager;
    },
    isTruckingOrganizationDispatcher: ({ user: { roles } }) => roles.isTruckingOrganizationDispatcher,
    hasTruckingOrganizationManagerAccess: ({ user: { roles } }) => {
        const { isTruckingOrganizationAdmin, isTruckingOrganizationManager, isTruckingOrganizationHeadLogist } = roles;
        return isTruckingOrganizationAdmin || isTruckingOrganizationManager || isTruckingOrganizationHeadLogist;
    },
    hasTruckingOrganizationWithoutViewersAccess: ({ user: { roles } }) => {
        const {
            isTruckingOrganizationAdmin,
            isTruckingOrganizationManager,
            isTruckingOrganizationHeadLogist,
            isTruckingOrganizationOperator,
        } = roles;
        return (
            isTruckingOrganizationAdmin ||
            isTruckingOrganizationManager ||
            isTruckingOrganizationHeadLogist ||
            isTruckingOrganizationOperator
        );
    },
    canChangeResponsible: ({ user: { roles, profile }, record: { createdById }, options: { action } }) => {
        const id = profile!.id;
        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist } = roles;
        if (action === "new") {
            return true;
        }
        return isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist || id === createdById;
    },
    canSeeMarginality: ({ user: { roles, profile } }) => {
        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist, isTruckingOrganizationManager } = roles;
        const { availableFeatureModules } = profile!;
        return (
            availableFeatureModules.includes(
                featureModuleTypesEnum.Marginality as keyof typeof featureModuleTypesEnum,
            ) &&
            (isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist || isTruckingOrganizationManager)
        );
    },
    isCargoTruck: ({ user: { roles }, options: { isPublic }, context }) => {
        return (
            !isPublic &&
            (context.hasTruckingOrganizationManagerAccess() ||
                roles.isTruckingOrganizationOperator ||
                roles.isTruckingOrganizationExtendedViewer)
        );
    },
    canEditPayment: ({ options: { action }, context }) => {
        return context.notOwner() && action === "edit";
    },
    canEditPaymentStatusInShow: ({ context }) => {
        return context.hasTruckingOrganizationManagerAccess();
    },
    isNewForm: ({ options: { action } }) => action === "new",
    isPrivateOrPublicTruckingOrgBid: ({ user, record, options }) => {
        const { type, truckingOrganizationId } = record;
        const { profile } = user;
        const { action, isPublic } = options;
        return action === "new"
            ? !isPublic
            : type === bidTypesEnum.Public && profile!.organizationId === truckingOrganizationId;
    },
    canSeeRoute: ({ user: { roles }, record: { bidPoints }, context }) => {
        const { isForwarder } = roles;
        const pointsPresent = bidPoints.some((item: BidPointViewModel) => item.geozone && item.geozone.address);
        return (context.isPrivateOrPublicTruckingOrgBid() || !isForwarder) && context.isCargoTruck() && pointsPresent;
    },
    canSeeEditRouteBtn: ({ options: { action }, context }) => {
        return !context.inFinishedStatus() && action === "edit";
    },
    canSeeRevertBtn: ({ user: { roles, organization }, context }) => {
        const { isRoot, isAdmin, isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist } = roles;
        const prohibitionOfRevertingBidForHeadLogist =
            (prohibitionOfRevertingBidForHeadLogistFlagVal & organization!.intFlags) ===
            prohibitionOfRevertingBidForHeadLogistFlagVal;
        return (
            (isRoot || isAdmin || isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist) &&
            (!isTruckingOrganizationHeadLogist || !prohibitionOfRevertingBidForHeadLogist) &&
            context.inFinishedStatus()
        );
    },
    canSeeRevertBtnWhenActive: ({ user: { roles, organization }, record: { status } }) => {
        const { isRoot, isAdmin, isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist } = roles;
        const { Planned, Started } = bidStatusesEnum;
        const prohibitionOfRevertingBidForHeadLogist =
            (prohibitionOfRevertingBidForHeadLogistFlagVal & organization!.intFlags) ===
            prohibitionOfRevertingBidForHeadLogistFlagVal;
        return (
            (isRoot || isAdmin || isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist) &&
            (!isTruckingOrganizationHeadLogist || !prohibitionOfRevertingBidForHeadLogist) &&
            status &&
            [Planned, Started].includes(status)
        );
    },
    canGetLogistTaskSettings: ({ user: { roles } }) => {
        const { isRoot, isAdmin, isTruckingOrganizationAdmin } = roles;
        return isRoot || isAdmin || isTruckingOrganizationAdmin;
    },
    canSeeLogistTaskSettings: ({ user: { roles }, options: { action } }) => {
        const { isRoot, isAdmin, isTruckingOrganizationAdmin } = roles;
        return isRoot || isAdmin || isTruckingOrganizationAdmin || action === "edit";
    },
    canSeeUpdateRefuelingBtn: ({ options: { refueling } }) => {
        return !!refueling && !!refueling.plannedRefuelings && refueling.plannedRefuelings.length > 0;
    },
    canSeeAddSyncStatusBtn: ({ user: { roles } }) => {
        const { isTruckingOrganizationAdmin } = roles;
        return isTruckingOrganizationAdmin;
    },
    canSeeAxisLoadValues: ({ user: { profile }, record: { car } }) => {
        const { availableFeatureModules } = profile!;
        return (
            availableFeatureModules.includes(
                featureModuleTypesEnum.AxisLoadMonitoring as keyof typeof featureModuleTypesEnum,
            ) &&
            car &&
            car.trackerId
        );
    },
    hasEbillsAccess: ({ user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return availableFeatureModules.includes(featureModuleTypesEnum.Waybills as keyof typeof featureModuleTypesEnum);
    },
    canSeeSalesManager: ({ user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return availableFeatureModules.includes(
            featureModuleTypesEnum.BidSalesManager as keyof typeof featureModuleTypesEnum,
        );
    },
    canSeeScenarioAnswers: ({ context, record: { bidPoints } }) => {
        return context.canSeeScenarios() && !!bidPoints.some((item: BidPointViewModel) => !!item.scenarioId);
    },
    canSeeTripScenarios: ({ user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return availableFeatureModules.includes(
            featureModuleTypesEnum.TripScenarios as keyof typeof featureModuleTypesEnum,
        );
    },
    canSeeScenarios: ({ user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return availableFeatureModules.includes(
            featureModuleTypesEnum.Scenarios as keyof typeof featureModuleTypesEnum,
        );
    },
    canSeeFactoring: ({ record: { status, bidPoints }, user: { profile } }) => {
        const lastPoint = bidPoints[bidPoints.length - 1];
        const lastPointLeaved = lastPoint.autoLeavedAt || lastPoint.loadUnloadedAtByLogist;
        const { availableFeatureModules } = profile!;
        return (
            !!lastPointLeaved &&
            status === bidStatusesEnum.Done &&
            availableFeatureModules.includes(featureModuleTypesEnum.Factoring as keyof typeof featureModuleTypesEnum)
        );
    },
    canSeePointPrediction: ({ record: { status }, user: { profile, organization }, point, previousPointVisited }) => {
        const { availableFeatureModules } = profile!;

        const allowManualNeuralNetworkUsageFlagValue = flagVal(
            organizationFlags,
            organizationFlagsEnum.AllowUnloadPointAdjustmentUsage,
        );
        const allowManualNeuralNetworkUsageEnabled =
            (allowManualNeuralNetworkUsageFlagValue & organization!.intFlags) ===
            allowManualNeuralNetworkUsageFlagValue;

        return (
            status === bidStatusesEnum.Started &&
            availableFeatureModules.includes(
                featureModuleTypesEnum.BidPointsAdjustment as keyof typeof featureModuleTypesEnum,
            ) &&
            allowManualNeuralNetworkUsageEnabled &&
            [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(point!.type) &&
            !point?.autoEnteredAt &&
            !point?.enteredAtByLogist &&
            (!!point?.enteredAt || !!previousPointVisited)
        );
    },
    canSeeDocumentAssignment: ({ user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return availableFeatureModules.includes(
            featureModuleTypesEnum.DocumentAssignment as keyof typeof featureModuleTypesEnum,
        );
    },
    isBidExecutor: ({ user: { profile }, record: { type, organizationId, truckingOrganizationId } }) => {
        return type === bidTypesEnum.Private
            ? organizationId === profile!.organizationId
            : truckingOrganizationId === profile!.organizationId;
    },
    canRunToWork: ({
        record: { id, status, approvedAt, isCreatedByOperator, isEmptyMileageBid },
        context,
        userPolicy,
        usesBidsApproval,
    }) => {
        return (
            (!usesBidsApproval || !isCreatedByOperator || approvedAt || !isEmptyMileageBid) && // если включено согласование и заявка не является порожней и заявка создана ролью Оператор, то заявка должна быть согласована
            context.hasTruckingOrganizationWithoutViewersAccess() &&
            !!id &&
            status === bidStatusesEnum.New &&
            context.isBidExecutor() &&
            userPolicy!.hasPlanningModule()
        );
    },
    canDelete: ({ record: { status }, context }) => context.isNewBid() || status === bidStatusesEnum.Planned,
    isNewBid: ({ record: { id, status } }) => !!id && status === bidStatusesEnum.New,
    canChangeStatus: ({ user: { roles }, options: { action } }) => {
        const { isRoot } = roles;
        return isRoot && action === "edit";
    },
    canEditAsCargo: ({ record: { isAccepted }, options: { action } }) => action !== "edit" || !isAccepted,
    canSeeHeadToWorkBtn: ({
        user: { profile },
        record: {
            status,
            type,
            organizationId,
            truckingOrganizationId,
            approvedAt,
            createdBy,
            isDeleted,
            isEmptyMileageBid,
        },
        usesBidsApproval,
        context,
    }) => {
        if (status !== bidStatusesEnum.New || !context.hasTruckingOrganizationWithoutViewersAccess() || isDeleted) {
            return false;
        }
        return (!usesBidsApproval ||
            (createdBy && !createdBy.isOperator) ||
            approvedAt ||
            (usesBidsApproval && isEmptyMileageBid)) &&
            type === bidTypesEnum.Private
            ? organizationId === profile!.organizationId
            : truckingOrganizationId === profile!.organizationId;
    },
    isMyBid: ({ user: { profile }, record: { type, organizationId } }) => {
        return organizationId === profile!.organizationId && type === bidTypesEnum.Public;
    },
    isOpened: ({ options: { isOpened } }) => isOpened!,
    canSeeAdditionalInfo: ({ user: { roles }, context }) => {
        const {
            isTruckingOrganizationAdmin,
            isTruckingOrganizationManager,
            isTruckingOrganizationOperator,
            isTruckingOrganizationHeadLogist,
            isTruckingOrganizationDispatcher,
            isTruckingOrganizationExtendedViewer,
        } = roles;
        return (
            !context.isOpened() &&
            (isTruckingOrganizationAdmin ||
                isTruckingOrganizationManager ||
                isTruckingOrganizationOperator ||
                isTruckingOrganizationHeadLogist ||
                isTruckingOrganizationDispatcher ||
                isTruckingOrganizationExtendedViewer)
        );
    },
    canSeeEditBtn: ({
        user: { profile, roles, organization },
        record: { status, type, organizationId, truckingOrganizationId, isDeleted },
        context,
    }) => {
        if (isDeleted || context.isReadOnlyTruckingOrganizationUser()) {
            return false;
        }

        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist } = roles;
        const headLogistHasAccess =
            isTruckingOrganizationHeadLogist &&
            (headLogistCanEditCompletedBidsFlagVal & organization!.intFlags) === headLogistCanEditCompletedBidsFlagVal;
        const { New, Planned, Started, Done } = bidStatusesEnum;
        if (
            type === bidTypesEnum.Public
                ? truckingOrganizationId === profile!.organizationId
                : organizationId === profile!.organizationId
        ) {
            return (
                status === New ||
                (type === bidTypesEnum.Private &&
                    (((isTruckingOrganizationAdmin || headLogistHasAccess) && status === Done) ||
                        (context.hasTruckingOrganizationManagerAccess() && [Started, Planned].includes(status))))
            );
        }
        return false;
    },
    isOnlyViewer: ({ user: { roles } }) => isOnlyInRole(roles, "isTruckingOrganizationViewer"),
    isOnlyExtendedViewer: ({ user: { roles } }) =>
        isOnlyInRole(roles, "isTruckingOrganizationExtendedViewer"),
    isReadOnlyTruckingOrganizationUser: ({ context }) =>
        context.isOnlyViewer() || context.isOnlyExtendedViewer(),
    canSeeCancelBtn: ({
        user: { profile },
        record: { status, organizationId, isDeleted },
        context,
    }) => {
        if (isTechSupport()) {
            return false;
        }
        if (organizationId !== profile!.organizationId || isDeleted) {
            return false;
        }

        const { Planned, Started } = bidStatusesEnum;
        return status && [Planned, Started].includes(status) && context.hasTruckingOrganizationManagerAccess();
    },
    canSeeDeleteBtn: ({ user: { profile, roles, organization }, record: { status, organizationId, isDeleted } }) => {
        if (isTechSupport()) {
            return false;
        }
        if (organizationId !== profile!.organizationId || isDeleted) {
            return false;
        }

        const { isTruckingOrganizationAdmin, isTruckingOrganizationManager, isTruckingOrganizationHeadLogist } = roles;
        const prohibitionOfBidDelete =
            (prohibitionOfBidDeleteFlagVal & organization!.intFlags) === prohibitionOfBidDeleteFlagVal;

        if (prohibitionOfBidDelete && !isTruckingOrganizationAdmin) {
            return false;
        }

        return (
            status === bidStatusesEnum.New ||
            (status === bidStatusesEnum.Planned &&
                (isTruckingOrganizationHeadLogist || isTruckingOrganizationManager || isTruckingOrganizationAdmin))
        );
    },
    canSeeRecalculationBtn: ({ record: { status, isDeleted }, context }) => {
        if (isDeleted) {
            return false;
        }

        const { Planned, Started, Done } = bidStatusesEnum;
        return context.hasTruckingOrganizationManagerAccess() && [Planned, Started, Done].includes(status);
    },
    canSeeFilterTrackerLogs: ({ record: { status }, context, user: { profile } }) => {
        const { availableFeatureModules } = profile!;

        const { Started, Done } = bidStatusesEnum;
        return (
            availableFeatureModules.includes(
                featureModuleTypesEnum.TrackerLogsFiltering as keyof typeof featureModuleTypesEnum,
            ) &&
            context.hasTruckingOrganizationManagerAccess() &&
            [Started, Done].includes(status)
        );
    },
    canSeeRequestCoordinates: ({ record: { status }, context }) => {
        const { Started, Done } = bidStatusesEnum;
        return context.hasTruckingOrganizationManagerAccess() && [Started, Done].includes(status);
    },
    canSeeTollSegmentInfo: ({ record: { route } }) =>
        !!route?.tollRoadSegments?.length ||
        route?.segments?.some((segment: { patternIds?: [] }) => !!segment.patternIds?.length),
    canSeeRemoveEmulatedSegments: ({ record }) => !!record.emulatedSegments?.length,
    canSeeСreateDocumentFromTemplate: ({ record: { cargoOwnerDictionaryItem }, context, user: { profile } }) => {
        const { availableFeatureModules } = profile!;

        return (
            cargoOwnerDictionaryItem &&
            context.hasTruckingOrganizationManagerAccess() &&
            availableFeatureModules.includes(featureModuleTypesEnum.CrStart as keyof typeof featureModuleTypesEnum)
        );
    },
    canSeeForceCompleteBtn: ({ record: { status, calculationStatus, isDeleted }, user: { roles } }) => {
        if (isDeleted) {
            return false;
        }

        const { Started, Done } = bidStatusesEnum;
        const { InProgress } = bidCalculationStatusesEnum;
        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist } = roles;
        return (
            (isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist) &&
            [Started, Done].includes(status) &&
            (calculationStatus === null || calculationStatus !== InProgress)
        );
    },
    canSeeEditPointDatesBtn: ({ record: { calculationStatus, status }, user: { roles } }) => {
        const { InProgress } = bidCalculationStatusesEnum;
        const { Done, Started } = bidStatusesEnum;
        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist } = roles;
        return (
            [Started, Done].includes(status) &&
            (isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist) &&
            (calculationStatus === null || calculationStatus !== InProgress)
        );
    },
    canSetBidEstimatedLeaveDate: ({ record: { status, calculationStatus }, point, last, context }) => {
        const { Done, Started } = bidStatusesEnum;
        const { autoLeavedAt, loadUnloadedAtByLogist } = point!;
        const { InProgress } = bidCalculationStatusesEnum;
        return (
            !(autoLeavedAt || loadUnloadedAtByLogist) &&
            context.hasTruckingOrganizationManagerAccess() &&
            [Started, Done].includes(status) &&
            last &&
            (calculationStatus === null || calculationStatus !== InProgress)
        );
    },
    canPlanRefuelings: ({ user: { profile }, refuelingServiceType, refuelingSettings }) => {
        const { availableFeatureModules } = profile!;
        return (
            availableFeatureModules.includes(
                featureModuleTypesEnum.RefuelingPlanning as keyof typeof featureModuleTypesEnum,
            ) &&
            !refuelingSettings!.hideRefuelingPlanning &&
            (!refuelingServiceType ||
                refuelingServiceType === bidRefuelingServiceTypesEnum.MultiGo ||
                refuelingServiceType === bidRefuelingServiceTypesEnum.MultiGo2 ||
                refuelingServiceType === bidRefuelingServiceTypesEnum.SolarFrps ||
                refuelingServiceType === bidRefuelingServiceTypesEnum.Str)
        );
    },
    canEditPlanRefuelings: ({ user: { profile, roles }, refuelingSettings }) => {
        const {
            isTruckingOrganizationAdmin,
            isTruckingOrganizationHeadLogist,
            isTruckingOrganizationManager,
            isTruckingOrganizationOperator,
        } = roles;
        const { availableFeatureModules } = profile!;
        return (
            availableFeatureModules.includes(
                featureModuleTypesEnum.RefuelingPlanning as keyof typeof featureModuleTypesEnum,
            ) &&
            !refuelingSettings!.hideRefuelingPlanning &&
            (isTruckingOrganizationAdmin ||
                isTruckingOrganizationHeadLogist ||
                isTruckingOrganizationManager ||
                isTruckingOrganizationOperator)
        );
    },
    canViewPlanRefuelings: ({ user: { profile }, refuelingSettings }) => {
        const { availableFeatureModules } = profile!;
        return (
            !refuelingSettings!.hideRefuelingPlanning &&
            availableFeatureModules.includes(
                featureModuleTypesEnum.RefuelingPlanning as keyof typeof featureModuleTypesEnum,
            )
        );
    },
    canDragPoint: ({ context, record: { status } }) =>
        context.isOwner() &&
        (context.canSeeEditBtn() || context.dispatcherCanDragPoint()) &&
        status !== bidStatusesEnum.Done,
    dispatcherCanDragPoint: ({
        user: { profile, roles },
        record: { status, type, organizationId, truckingOrganizationId, isDeleted },
        context,
    }) => {
        if (isDeleted || context.isReadOnlyTruckingOrganizationUser()) {
            return false;
        }

        const { isTruckingOrganizationDispatcher } = roles;
        const { New, Planned, Started } = bidStatusesEnum;
        if (
            type === bidTypesEnum.Public
                ? truckingOrganizationId === profile!.organizationId
                : organizationId === profile!.organizationId
        ) {
            return (
                status === New ||
                (type === bidTypesEnum.Private &&
                    isTruckingOrganizationDispatcher &&
                    [Started, Planned].includes(status))
            );
        }
        return false;
    },
    needPreloadDataByCoupling: ({ user: { profile }, changedData }) => {
        const { name } = changedData!;
        return (
            name === "coupling" &&
            profile!.availableFeatureModules.includes(
                featureModuleTypesEnum.TripCoupling as keyof typeof featureModuleTypesEnum,
            )
        );
    },
    needPreloadDataByCar: ({ record: { driver }, changedData }) => {
        const { name, value } = changedData!;
        return name === "carOption" && value && value.carId && !(driver && driver.id);
    },
    needPreloadDataByDriver: ({ record: { carOption, driver }, changedData }) => {
        const { name, value } = changedData!;
        return (
            name === "driver" &&
            value &&
            value.id &&
            !(carOption && carOption.carId) &&
            !(driver && value.id === driver.id)
        );
    },
    needPreloadDataByCargoOwner: ({ changedData }) => {
        const { name, value } = changedData!;
        return name === "cargoOwnerDictionaryItemId" && value;
    },
    needUpdateCarAvailability: ({ value, user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return (
            !!value &&
            availableFeatureModules.includes(
                featureModuleTypesEnum.PlanningCarSchedule as keyof typeof featureModuleTypesEnum,
            )
        );
    },
    needUpdateTrailerAvailability: ({ value, user: { organization } }) => {
        return !!value && !!organization?.flags?.includes(organizationFlagsEnum.UseTrailerMileage);
    },
    canSeeCriticalMileageBtn: ({ record: { status, isDeleted }, options: { distance } }) => {
        if (isDeleted) {
            return false;
        }

        return !!distance && status === bidStatusesEnum.Done;
    },
    canSeeApproveBtn: ({
        record: { approvedAt, status, createdBy, isDeleted, isEmptyMileageBid },
        user: { roles },
        usesBidsApproval,
    }) => {
        const { isTruckingOrganizationOperator, isTruckingOrganizationAdmin } = roles;
        return (
            !isEmptyMileageBid &&
            usesBidsApproval &&
            (isTruckingOrganizationOperator || isTruckingOrganizationAdmin) &&
            status === bidStatusesEnum.New &&
            !approvedAt &&
            createdBy &&
            createdBy.isOperator &&
            !isDeleted
        );
    },
    canSeeBidItemChanges: ({ context }) => {
        return context.hasTruckingOrganizationManagerAccess();
    },
    disabledApproveBtn: ({ user: { roles }, record: { syncStatusData } }) => {
        const { isTruckingOrganizationAdmin } = roles;
        if (isTruckingOrganizationAdmin) {
            return false;
        }
        return !syncStatusData || syncStatusData.syncStatus === syncEntityStatusesEnum.Failed;
    },
    canSeeDisapproveBtn: ({
        record: { approvedAt, status, createdBy, isDeleted },
        user: { roles },
        usesBidsApproval,
    }) => {
        const { isTruckingOrganizationOperator, isTruckingOrganizationAdmin } = roles;
        return (
            usesBidsApproval &&
            (isTruckingOrganizationOperator || isTruckingOrganizationAdmin) &&
            status === bidStatusesEnum.New &&
            approvedAt &&
            createdBy &&
            createdBy.isOperator &&
            !isDeleted
        );
    },
    canSaveAsTemplate: ({ record: { bidPoints, route, isDeleted }, user: { roles } }) => {
        if (!route || isDeleted) {
            return false;
        }
        const { segments } = route;
        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist } = roles;
        return (
            (isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist) && segments.length > bidPoints.length - 1
        );
    },
    canSeeRestoreBtn: ({
        user: { profile, roles },
        record: { isDeleted, type, organizationId, truckingOrganizationId },
    }) => {
        const { isTruckingOrganizationAdmin, isRoot } = roles;
        if (
            type === bidTypesEnum.Public
                ? truckingOrganizationId === profile!.organizationId
                : organizationId === profile!.organizationId
        ) {
            return type === bidTypesEnum.Private && isDeleted && (isTruckingOrganizationAdmin || isRoot);
        }
        return false;
    },
    canUseMultipleBidCargos: ({ user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return availableFeatureModules.includes(
            featureModuleTypesEnum.MultipleBidCargos as keyof typeof featureModuleTypesEnum,
        );
    },
    hasPlanEmptyMileageBidModule: ({ user: { profile } }) => {
        const { availableFeatureModules } = profile!;
        return availableFeatureModules.includes(
            featureModuleTypesEnum.PlanEmptyMileageBid as keyof typeof featureModuleTypesEnum,
        );
    },
    canSeeCargoOwnerShareAccessInEdit: ({ user: { profile, roles }, record }) => {
        const { availableFeatureModules } = profile!;
        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist, isTruckingOrganizationManager } = roles;
        return (
            availableFeatureModules.includes(
                featureModuleTypesEnum.Forwarding as keyof typeof featureModuleTypesEnum,
            ) &&
            (isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist || isTruckingOrganizationManager) &&
            ["", bidStatusesEnum.New, bidStatusesEnum.Planned, bidStatusesEnum.Started].includes(record.status) &&
            record.cargoOwnerDictionaryItemId &&
            record.cargoOwnerShareAccess &&
            !record.cargoOwnerShareAccess.hasAccessToBids
        );
    },
    canSeeCargoOwnerShareAccessInShow: ({ user: { profile, roles }, record }) => {
        const { availableFeatureModules } = profile!;
        const { isTruckingOrganizationAdmin, isTruckingOrganizationHeadLogist, isTruckingOrganizationManager } = roles;
        return (
            availableFeatureModules.includes(
                featureModuleTypesEnum.Forwarding as keyof typeof featureModuleTypesEnum,
            ) &&
            (isTruckingOrganizationAdmin || isTruckingOrganizationHeadLogist || isTruckingOrganizationManager) &&
            [bidStatusesEnum.New, bidStatusesEnum.Planned, bidStatusesEnum.Started].includes(record.status) &&
            record.cargoOwnerDictionaryItem &&
            !record.cargoOwnerDictionaryItem.hasAccessToBids
        );
    },
});

export default BidPolicy;
